import { Button } from 'framework/ui/mui-builder';
import { useState } from 'react';
import { ModalForm } from 'framework/form';
import { Modal } from 'framework/ui/ui-builder';
import { Typography } from '@mui/material';
import { useNewProjectUrl } from 'domain/shared/newProject';
import { getRenderingApiUrl } from 'infrastructure/pdf';
import useChangeRequestService from '../changeRequestService';
import { useBlobPreview, BlobPreviewModal } from 'framework/ui/Blob';
import { generatePdf, PlaceholderValues } from 'framework/markdownEditor';

interface SignButtonProps {
  projectId?: string;
  requestId?: string;
  content: string;
  placeholderValues: Nullable<PlaceholderValues>;
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string)?.split(',')[1];
      resolve(base64 ?? '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const SignButton = ({ projectId, requestId, content, placeholderValues }: SignButtonProps) => {
  const [formModalOpen, setFormModalOpen] = useState(false);
  const { getSingleEntityUrl } = useNewProjectUrl();
  const { sendForSigningChangeRequest } = useChangeRequestService();

  const { blobUrl, loading, setLoading, previewOpen, setPreviewOpen, closePreview, createBlobUrl } = useBlobPreview();

  const generatePdfBlob = async () => {
    setLoading(true);
    try {
      const blob = await generatePdf(getRenderingApiUrl(), content, placeholderValues);
      createBlobUrl(blob);
      return blob;
    } finally {
      setLoading(false);
    }
  };

  const openPreview = () => {
    setPreviewOpen(true);
    if (!blobUrl) generatePdfBlob();
  };

  const openFormModal = () => {
    closePreview();
    setFormModalOpen(true);
  };

  return (
    <>
      <Button variant="contained" onClick={openPreview} disabled={loading}>
        Send to DocuSign
      </Button>
      <Modal
        hasCancelButton
        isOpen={formModalOpen}
        onToggle={() => setFormModalOpen(false)}
        title="The document will be sent for signature via DocuSign"
      >
        <ModalForm
          closeModal={() => setFormModalOpen(false)}
          modalOpen={formModalOpen}
          getDataFunc={async () => {
            const blob = blobUrl ? undefined : await generatePdfBlob();
            return { pdfBlob: blob };
          }}
          submitFormFunc={async (formData: { pdfBlob?: Blob }) => {
            let base64Content: string;
            if (formData.pdfBlob) {
              base64Content = await blobToBase64(formData.pdfBlob);
            } else if (blobUrl) {
              const response = await fetch(blobUrl);
              const blob = await response.blob();
              base64Content = await blobToBase64(blob);
            } else {
              return;
            }
            return sendForSigningChangeRequest(requestId!, { content: base64Content });
          }}
          afterSubmit={{
            redirect: getSingleEntityUrl(projectId!),
            message: 'The Change Request has been successfully sent via DocuSign for signature.',
          }}
        >
          <Typography pb={4}>
            {`After clicking "Submit", the Change Request document will be automatically generated as a PDF and sent to ${placeholderValues?.receiverFullName} via DocuSign for electronic signature.`}
          </Typography>
        </ModalForm>
      </Modal>
      <BlobPreviewModal
        open={previewOpen}
        blobUrl={blobUrl}
        fileName="Change Request.pdf"
        onClose={closePreview}
        loading={loading}
        approvalFooter={{
          onNext: openFormModal,
          onCancel: closePreview,
          sendButtonText: 'Next',
          approvalLabel: 'I have reviewed the document and approve sending it for signature',
        }}
      />
    </>
  );
};

export default SignButton;
